import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { AirportsResponseVm, AirportsResponse, CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { FilterApplier } from '@shared/flight-itinerary/entities/filters';
import { TripTypes } from '@app/entities/trip-types';
import { AirportsFilter, AirportSectorFilter } from '@flight-results/entities/request-search.payload';

@Component({
  selector: 'app-sector-checkbox-filter',
  templateUrl: './sector-checkbox-filter.component.html',
  styleUrls: ['./sector-checkbox-filter.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SectorCheckboxFilterComponent implements OnChanges {

  @Input() isBuses: boolean;
  @Input() label: string;
  @Input() departureCriteria: string;
  @Input() returnCriteria: string;
  @Input() type: string;
  @Input() options: AirportsResponseVm[] | AirportsResponse[] = [];
  @Input() index = -1;
  @Input() isMobile = false;
  @Input() pickedCheckboxes: AirportsFilter = {} as AirportsFilter;
  @Input() isOpenBox: boolean;
  @Input() tripType: TripTypes;
  @Input() individualFilter = false;

  @Output() filter: EventEmitter<FilterApplier> = new EventEmitter<FilterApplier>();

  public activeDisabled = false;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translateService: TranslocoService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['pickedCheckboxes'] && changes['pickedCheckboxes'].currentValue.length === 0) {
      this.activeDisabledReset();
      this.detectChanges();
    }
  }

  public activeDisabledReset(): void {
    this.activeDisabled = false;
  }

  private detectChanges(): void {
    this.changeDetector.detectChanges();
  }

  get tripReturn(): TripTypes {
    return TripTypes.RETURN;
  }

  get sectorOptions(): CheckboxFilter[] {
    if (this.tripType === TripTypes.DEPARTURE) {
      return this.departureSectorOptions;
    }
    return this.returnSectorOptions;
  }

  get departureSectorOptions(): CheckboxFilter[] {
    const departureSectorAirports = this.options && this.options[0] ? this.options[0].Airports : [];
    if (departureSectorAirports) {
      if (isNullOrUndefined(departureSectorAirports) || departureSectorAirports.length <= 1) {
        return null;
      }
      return departureSectorAirports.map(item => {
        return {
          Text: item.Text,
          Count: item.Count,
          TextTransformed: item.TextTransformed,
          MinPrice: item.MinPrice,
          Stops: item.Stops,
          Label: this.isBuses ? item.AirportName : `${item.Text} - ${item.AirportName}`
        };
      });
    } else {
      return null;
    }
  }

  get returnSectorOptions(): CheckboxFilter[] {
    if (isNullOrUndefined(this.options) || this.options.length <= 1) {
      return null;
    }
    const returnSectorAirports = this.options[1].Airports;
    if (isNullOrUndefined(returnSectorAirports) || returnSectorAirports.length <= 1) {
      return null;
    }
    return returnSectorAirports.map(item => ({
      Text: item.Text,
      Count: item.Count,
      TextTransformed: item.TextTransformed,
      MinPrice: item.MinPrice,
      Stops: item.Stops,
      Label: this.isBuses ? item.AirportName : `${item.Text} - ${item.AirportName}`
    }));
  }

  get pickedSectorCheckboxes(): string[] {
    if (this.tripType === TripTypes.DEPARTURE) {
      return this.pickedDepartureCheckboxes;
    }
    return this.pickedReturnCheckboxes;
  }

  get pickedDepartureCheckboxes(): string[] {
    if (isNullOrUndefined(this.pickedCheckboxes)) {
      return [];
    }
    const sector = this.pickedCheckboxes?.[this.type]?.find((x: AirportSectorFilter) => x.SectorNumber === TripTypes.DEPARTURE);
    if (isNullOrUndefined(sector) || isNullOrUndefined(sector.Airports)) {
      return [];
    }
    return sector.Airports;
  }

  get pickedReturnCheckboxes(): string[] {
    if (isNullOrUndefined(this.pickedCheckboxes)) {
      return [];
    }
    const sector = this.pickedCheckboxes?.[this.type]?.find((x: AirportSectorFilter) => x.SectorNumber === TripTypes.RETURN);
    if (isNullOrUndefined(sector) || isNullOrUndefined(sector.Airports)) {
      return [];
    }
    return sector.Airports;
  }

  get getLabel(): string {
    if (this.tripType === TripTypes.DEPARTURE) {
      return this.getDepartureLabel(this.label);
    }
    return this.getReturnLabel(this.label);
  }

  private getDepartureLabel(label: string): string {
    if (this.tripType === TripTypes.DEPARTURE && this.returnSectorOptions) {
      return this.translateService.translate(label, { filterCriteria: this.departureCriteria });
    }
    return this.translateService.translate(label, { filterCriteria: '' });
  }

  private getReturnLabel(label: string): string {
    if (this.departureSectorOptions) {
      return this.translateService.translate(label, { filterCriteria: this.returnCriteria });
    }
    return this.translateService.translate(label, { filterCriteria: '' });
  }

  public applyCheckBoxFilter(filter: FilterApplier): void {
    this.filter.emit({ ...filter, sector: this.tripType });
  }

}
